import React, {
  ReactNode,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { isMobile } from "react-device-detect";
import useIntersectionObserver from "@react-hook/intersection-observer";
import styled from "styled-components";
import Image from "next/image";
import { useTheme } from "lib/contexts/themeContext";
import PageLayout from "lib/components/PageLayout";
import ModalDownloadApp from "lib/components/ModalDownloadApp";
import QRAppDownloadMockInterview from "lib/assets/qr-app-download-mock-interview.svg";
import Button from "lib/components/Button";
import Stack from "lib/components/Stack";
import useScript from "lib/hooks/useScript";
import IconRibbon from "lib/assets/ribbon.svg";
import IconStar from "lib/assets/landing-star.svg";
import IconCog from "lib/assets/cogwheel.svg";
import rive1 from "lib/assets/rive/interview1.riv";
import rive2 from "lib/assets/rive/interview2.riv";
import rive3 from "lib/assets/rive/interview3.riv";
import rive4 from "lib/assets/rive/interview4.riv";
import ImgMockDevice from "lib/assets/interview-mock-device.png";
import ImgScreenshot from "lib/assets/interview-screenshot.jpg";
import IconVisaFamilyBased from "lib/assets/visa-family-based.svg";
import IconVisaEmploymentBased from "lib/assets/visa-employment-based.svg";
import IconVisaNonImmigrant from "lib/assets/visa-non-immigrant.svg";
import IconVisaOthers from "lib/assets/visa-others.svg";

import { UIEvent } from "lib/enums";
import { useEvent } from "lib/contexts/eventContext";
import SnackbarDownloadApp from "lib/components/SnackbarDownloadApp";
import Head from "lib/components/Head";
import ResourceLink from "lib/components/ResourceLink";
import { getAirbridgeTrackerLink } from "lib/airbridge";
import { useTranslations } from "lib/i18n";

const ViewMockInterview = () => {
  const t = useTranslations("mockInterview");
  return (
    <PageLayout>
      <Head
        title={t("meta.title")}
        desc={t("meta.desc")}
        url="/mock-interview"
      />
      <Hero />
      <AskProfessional />
      <Video />
      <Description />
      <TypesVisa />
      <HowItWorks />
      <AskProfessional2 />
      <Resources />
    </PageLayout>
  );
};

export default ViewMockInterview;

const Hero = () => {
  const t = useTranslations("mockInterview");
  const { breakpoints, palette } = useTheme();

  return (
    <div
      css={`
        margin: 120px auto 160px;
        padding: 0 20px;
        ${breakpoints.down("sm")} {
          margin: 80px auto 100px;
        }
      `}
      id="section1"
    >
      <h1
        css={`
          margin: 0 auto 20px;
          max-width: 1000px;
          display: flex;
          flex-direction: column;
          line-height: 80px;
          font-size: 60px;
          font-weight: bold;
          ${breakpoints.down("sm")} {
            margin: 0 auto 30px;
            line-height: 56px;
            font-size: 40px;
          }
        `}
      >
        <span style={{ color: palette.primary.main }}>{t("hero.line1")}</span>
        <span>{t("hero.line2")}</span>
        <span>{t("hero.line3")}</span>
      </h1>
      <div
        css={`
          position: relative;
          margin: 0 auto;
          padding-left: 60px;
          max-width: 1120px;
          display: flex;
          justify-content: space-between;
          ${breakpoints.down(1220)} {
            max-width: 1000px;
            padding-left: 0;
          }
          ${breakpoints.down("sm")} {
            flex-direction: column;
          }
        `}
      >
        <div
          css={`
            margin-top: 10px;
            line-height: 34px;
            font-size: 24px;
            font-weight: 700;
            white-space: pre-line;
            ${breakpoints.down("sm")} {
              margin: 0 0 60px 0;
              line-height: 28px;
              font-size: 20px;
            }
          `}
        >
          {t("hero.tagline")}
        </div>
        <div
          css={`
            position: relative;
            margin-top: -25px;
            margin-right: 25px;
            width: 60vw;
            max-width: 600px;
            ${breakpoints.down("sm")} {
              margin: 0 auto;
              width: 100%;
              max-width: 500px;
            }
          `}
        >
          <PhoneMockup />
          <div
            css={`
              position: absolute;
              right: 3.5%;
              top: -29.5%;
              ${breakpoints.down("sm")} {
                display: none;
              }
            `}
          >
            <IconStar />
          </div>
          <div
            css={`
              position: absolute;
              left: -43%;
              bottom: 16%;
              ${breakpoints.down("sm")} {
                display: none;
              }
            `}
          >
            <IconCog />
          </div>
        </div>
      </div>
    </div>
  );
};

const PhoneMockup = () => {
  return (
    <div
      css={`
        position: relative;
      `}
    >
      <div
        css={`
          position: absolute;
          top: 4.5%;
          left: 36.5%;
          width: 45.5%;
          overflow: hidden;
        `}
      >
        <Image
          unoptimized={!!process.env.NEXT_PUBLIC_NETLIFY}
          priority
          src={ImgScreenshot}
          width={2.5 * 272}
          height={2.5 * 440}
        />
      </div>
      <Image
        css={`
          position: relative;
          z-inex: 0;
        `}
        unoptimized={!!process.env.NEXT_PUBLIC_NETLIFY}
        priority
        src={ImgMockDevice}
        width={1200}
        height={920}
      />
    </div>
  );
};

const AskProfessional = () => {
  const t = useTranslations("mockInterview");
  const { track } = useEvent();
  const { breakpoints } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOnClick = useCallback(() => {
    if (isMobile) {
      window.open(
        getAirbridgeTrackerLink(true, "Mock Interview", "Cloud CTA Top"),
        "_blank",
      );
      track(UIEvent.MockInterviewMobileAppDownload);
    } else {
      setIsModalOpen(true);
      sessionStorage.setItem("disableDownloadApp", "true");
      track(UIEvent.MockInterviewDesktopAppDownload);
    }
  }, [track]);

  const handleOnClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <div
      css={`
        overflow: hidden;
        position: relative;
        margin: 0 auto 200px;
        max-width: 940px;
        height: 304px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        ${breakpoints.down("sm")} {
          margin: 0 auto 140px;
          max-width: 1152px;
          height: 398px;
        }
      `}
      id="section3"
    >
      <div
        css={`
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 940px;
          height: 304px;
          ${breakpoints.down("sm")} {
            transform: translate(-50%, -50%) scale(1.3);
          }
        `}
      >
        <IconRibbon
          css={`
            path {
              fill: #ebefff;
            }
          `}
        />
      </div>

      <div
        css={`
          position: relative;
        `}
      >
        <div
          css={`
            margin: 0 20px 20px;
            line-height: 28px;
            font-size: 20px;
            font-weight: 700;
          `}
        >
          {t("ask.question")}
        </div>
        <div
          css={`
            margin: 0 auto;
            padding: 0 20px;
            width: 300px;
            display: flex;
            justify-content: center;
            align-items: center;
            ${breakpoints.down("sm")} {
              width: 100%;
              max-width: 322px;
            }
          `}
        >
          <ButtonLight onClick={handleOnClick}>{t("ask.cta")}</ButtonLight>
        </div>
      </div>

      <ModalDownloadApp
        qr={QRAppDownloadMockInterview}
        open={isModalOpen}
        onClose={handleOnClose}
      />
    </div>
  );
};

const ButtonLight = styled(Button)`
  &&.MuiButton-root {
    height: 60px;
    line-height: 26px;
    font-size: 18px;
    font-weight: 500;
    background-color: #325aff;
    :hover {
      background-color: #325aff;
    }
  }
`;

interface DescriptionItemProps {
  bg: string;
  index: number;
  rive: any;
  animationReady: boolean;
  reverse?: boolean;
}

const DescriptionItem: React.FC<DescriptionItemProps> = ({
  bg,
  index,
  rive,
  animationReady,
  reverse,
  children,
}) => {
  const { breakpoints } = useTheme();
  const container = useRef<HTMLDivElement>(null);
  const [isAnimPlaying, setIsAnimPlaying] = useState(false);
  const { isIntersecting } = useIntersectionObserver(container, {
    threshold: 0.3,
  });

  useEffect(() => {
    if (animationReady) {
      if (isIntersecting) {
        setIsAnimPlaying(true);
      } else {
        setIsAnimPlaying(false);
      }
    }
  }, [animationReady, index, isIntersecting, rive]);

  return (
    <DescriptionItemContainer
      css={`
        background-color: ${bg};
      `}
      ref={container}
    >
      <div
        css={`
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 20px;
          width: 100%;
          max-width: 1000px;
          ${breakpoints.down("sm")} {
            margin: 60px 0;
            flex-direction: column;
          }
        `}
      >
        <div
          css={`
            width: 100%;
            max-width: 490px;
            order: ${reverse ? 2 : 1};
            ${breakpoints.down("sm")} {
              margin-bottom: 20px;
              order: 1;
            }
          `}
        >
          {children}
        </div>
        <div
          css={`
            width: 100%;
            max-width: 400px;
            order: ${reverse ? 1 : 2};
            ${breakpoints.down("sm")} {
              order: 2;
              display: flex;
              justify-content: center;
            }
          `}
        >
          <div
            css={`
              width: 100%;
              height: 0;
              padding-bottom: 75%;
            `}
          >
            <div
              css={`
                max-width: 400px;
              `}
            >
              {isAnimPlaying && (
                <RiveCanvas id={`riveCanvas${index}`} src={rive} />
              )}
            </div>
          </div>
        </div>
      </div>
    </DescriptionItemContainer>
  );
};

interface RiveCanvasProps {
  id: string;
  src: any;
}

const RiveCanvas: React.FC<RiveCanvasProps> = ({ id, src }) => {
  useEffect(() => {
    new window.rive.Rive({
      src,
      canvas: document.getElementById(id),
      layout: new window.rive.Layout({ fit: "contain" }),
      autoplay: true,
    });
  }, [id, src]);

  return <canvas style={{ width: "100%" }} id={id} width={800} height={600} />;
};

const DescriptionItemContainer = styled.div`
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 42px;
  font-size: 30px;
  font-weight: bold;
  color: white;
  ${({ theme }) => theme.breakpoints.down("sm")} {
    height: auto;
    line-height: 34px;
    font-size: 24px;
  }
`;

const Description = () => {
  const t = useTranslations("mockInterview");
  const { breakpoints } = useTheme();
  const status = useScript("https://unpkg.com/rive-js@0.7.11/dist/rive.min.js");
  const container = useRef<HTMLDivElement>(null);
  const isRiveReady = useMemo(() => status === "ready", [status]);
  const [isSnackbarRendered, setIsSnackbarRendered] = useState(false);
  const { isIntersecting } = useIntersectionObserver(container, {
    threshold: 0.1,
  });

  useEffect(() => {
    if (!isSnackbarRendered && isIntersecting) {
      setIsSnackbarRendered(true);
    }
  }, [isSnackbarRendered, isIntersecting]);

  return (
    <div
      css={`
        margin-bottom: 158px;
        ${breakpoints.down("sm")} {
          margin-bottom: 100px;
        }
      `}
      id="section5"
      ref={container}
    >
      <DescriptionItem
        index={0}
        bg="#aa00ff"
        animationReady={isRiveReady}
        rive={rive1}
      >
        <DescriptionItemTitle>
          {t("features.officersTitle")}
        </DescriptionItemTitle>
        <DescriptionItemSubtitle>
          {t("features.officersBody")}
        </DescriptionItemSubtitle>
      </DescriptionItem>
      <DescriptionItem
        index={1}
        bg="#ff783c"
        animationReady={isRiveReady}
        reverse
        rive={rive2}
      >
        <DescriptionItemTitle>
          {t("features.visaTypesTitle")}
        </DescriptionItemTitle>
        <DescriptionItemSubtitle>
          {t("features.visaTypesBody")}
        </DescriptionItemSubtitle>
      </DescriptionItem>
      <DescriptionItem
        index={2}
        bg="#6400ff"
        animationReady={isRiveReady}
        rive={rive3}
      >
        <DescriptionItemTitle>
          {t("features.languagesTitle")}
        </DescriptionItemTitle>
        <DescriptionItemSubtitle>
          {t("features.languagesBody")}
        </DescriptionItemSubtitle>
      </DescriptionItem>
      <DescriptionItem
        index={3}
        bg="#00b4ff"
        reverse
        animationReady={isRiveReady}
        rive={rive4}
      >
        <DescriptionItemTitle>
          {t("features.durationTitle")}
        </DescriptionItemTitle>
        <DescriptionItemSubtitle>
          {t("features.durationBody")}
        </DescriptionItemSubtitle>
      </DescriptionItem>
      {/* Snackbar */}
      {isSnackbarRendered && <SnackbarDownloadApp />}
    </div>
  );
};

const DescriptionItemTitle = styled.div`
  margin-bottom: 20px;
  line-height: 42px;
  font-size: 30px;
  font-weight: 700;
  ${({ theme }) => theme.breakpoints.down("sm")} {
    padding-right: 20px;
    line-height: 34px;
    font-size: 24px;
  }
`;

const DescriptionItemSubtitle = styled.div`
  padding-right: 20px;
  line-height: 28px;
  font-size: 20px;
  font-weight: 500;
  ${({ theme }) => theme.breakpoints.down("sm")} {
    line-height: 24px;
    font-size: 16px;
    font-weight: 500;
  }
`;

const TypesVisa = () => {
  const t = useTranslations("mockInterview");
  const { breakpoints } = useTheme();

  return (
    <div
      css={`
        margin: 0 auto 160px;
        padding: 0 20px;
        max-width: 1000px;
        ${breakpoints.down("sm")} {
          margin-bottom: 100px;
        }
      `}
      id="section6"
    >
      <SectionTitle>{t("visaTypes.heading")}</SectionTitle>
      <div
        css={`
          display: grid;
          grid-template-columns: 1fr 1fr 1fr 1fr;
          column-gap: 22px;
          text-align: center;
          ${breakpoints.down("sm")} {
            margin-bottom: 87px;
            grid-template-columns: 1fr;
          }
        `}
      >
        <TypesItem
          image={<IconVisaFamilyBased />}
          title={t("visaTypes.family")}
          description="IR-1, CR-1, IR-2, IR-5, F1, F2A, F2B, F3, F4, K1/K2"
        />
        <TypesItem
          image={<IconVisaEmploymentBased />}
          title={t("visaTypes.employment")}
          description="EB1-1, EB1-2, EB1-3, EB2-1, EB2-NIW, EB3-1, EB3-2, EB3-3, EB4, EB5"
        />
        <TypesItem
          image={<IconVisaNonImmigrant />}
          title={t("visaTypes.nonImmigrant")}
          description="H-1B, H-1C, H-2A, H-2B, H-3, TN, DV, Blanket L(L-1, L-2), O, P, R, E-3, K-1, K-2, K-3, K-4, F, J, M"
        />
        <TypesItem
          image={<IconVisaOthers />}
          title={t("visaTypes.others")}
          description="Asylum, Refugee, T, U, VAWA, Q, E, Naturalization"
        />
      </div>
    </div>
  );
};

interface TypesItemProps {
  image: ReactNode;
  title: ReactNode;
  description: string;
}

const TypesItem: React.FC<TypesItemProps> = ({ image, title, description }) => {
  const { breakpoints } = useTheme();

  return (
    <div
      css={`
        margin-top: 30px;
        ${breakpoints.down("sm")} {
          margin-bottom: 60px;
          :last-of-type {
            margin-bottom: 0;
          }
        }
      `}
    >
      <div
        css={`
          margin-bottom: 30px;
        `}
      >
        {image}
      </div>
      <div
        css={`
          margin-bottom: 20px;
          min-height: 68px;
          line-height: 34px;
          font-size: 24px;
          font-weight: 500;
          white-space: pre-line;
          ${breakpoints.down("sm")} {
            min-height: 0;
          }
        `}
      >
        {title}
      </div>
      <div
        css={`
          line-height: 24px;
          font-size: 16px;
        `}
      >
        {description}
      </div>
    </div>
  );
};

const HowItWorks = () => {
  const t = useTranslations("mockInterview");
  const { breakpoints } = useTheme();

  return (
    <div
      css={`
        margin: 0 auto 160px;
        padding: 0 20px;
        max-width: 1040px;
        ${breakpoints.down("sm")} {
          margin-bottom: 100px;
        }
      `}
      id="section7"
    >
      <SectionTitle>{t("steps.heading")}</SectionTitle>
      <div
        css={`
          display: grid;
          grid-template-columns: 1fr 1fr;
          column-gap: 20px;
          row-gap: 21px;
          ${breakpoints.down("sm")} {
            grid-template-columns: 1fr;
          }
        `}
      >
        <HowItWorksItem
          step={1}
          title={t("steps.step1Title")}
          content={t("steps.step1Body")}
        />
        <HowItWorksItem
          step={2}
          title={t("steps.step2Title")}
          content={t("steps.step2Body")}
        />
        <HowItWorksItem
          step={3}
          title={t("steps.step3Title")}
          content={t("steps.step3Body")}
        />
        <HowItWorksItem
          step={4}
          title={t("steps.step4Title")}
          content={t("steps.step4Body")}
        />
      </div>
    </div>
  );
};

interface HowItWorksItemProps {
  step: number;
  title: string;
  content: string;
}

const HowItWorksItem: React.FC<HowItWorksItemProps> = ({
  step,
  title,
  content,
}) => {
  const t = useTranslations("mockInterview");
  const { breakpoints } = useTheme();

  return (
    <div
      css={`
        border-radius: 10px;
        padding: 30px;
        display: flex;
        flex-direction: column;
        background-color: #eff4ff;
        ${breakpoints.down("sm")} {
          padding: 30px;
        }
      `}
    >
      <div
        css={`
          margin-bottom: 9px;
          line-height: 22px;
          font-size: 14px;
          font-weight: 500;
          color: #325aff;
        `}
      >
        {t("steps.label", { step })}
      </div>
      <div
        css={`
          margin-bottom: 20px;
          line-height: 34px;
          font-size: 24px;
          font-weight: 500;
        `}
      >
        {title}
      </div>
      <div
        css={`
          line-height: 24px;
          font-size: 16px;
        `}
      >
        {content}
      </div>
    </div>
  );
};

const AskProfessional2 = () => {
  const t = useTranslations("mockInterview");
  const { track } = useEvent();
  const { breakpoints } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOnClick = useCallback(() => {
    if (isMobile) {
      window.open(
        getAirbridgeTrackerLink(true, "Mock Interview", "Cloud CTA Bottom"),
        "_blank",
      );
      track(UIEvent.MockInterviewMobileAppDownload);
    } else {
      setIsModalOpen(true);
      sessionStorage.setItem("disableDownloadApp", "true");
      track(UIEvent.MockInterviewDesktopAppDownload);
    }
  }, [track]);

  const handleOnClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <div
      css={`
        margin: 0 auto 160px;
        position: relative;
        max-width: 940px;
        overflow: hidden;
        ${breakpoints.down("sm")} {
          margin: 0 auto 96px;
          max-width: 1227px;
        }
      `}
      id="section8"
    >
      <div
        css={`
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 940px;
          ${breakpoints.down("sm")} {
            width: 1227px;
          }
        `}
      >
        <Cloud />
      </div>
      <div
        css={`
          padding: 0 20px;
          height: 310px;
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          ${breakpoints.down("sm")} {
            height: 398px;
          }
        `}
      >
        <div
          css={`
            margin-bottom: 16px;
            line-height: 28px;
            font-size: 20px;
            font-weight: 700;
            text-align: center;
          `}
        >
          {t("download.heading")}
        </div>
        <div
          css={`
            margin-bottom: 10px;
            width: 100%;
            display: flex;
            justify-content: center;
            ${breakpoints.down("sm")} {
              flex-direction: column;
              width: 100%;
            }
          `}
        >
          <div
            css={`
              margin: 0 0 10px 0;
              width: 100%;
              max-width: 280px;
              ${breakpoints.down("sm")} {
                margin: 0 auto 10px;
                width: 100%;
                max-width: 322px;
              }
            `}
          >
            <Button
              css={`
                &&& {
                  height: 60px;
                  line-height: 26px;
                  font-size: 18px;
                  font-weight: 500;
                  background-color: #00b4ff;
                }
              `}
              fullWidth
              onClick={handleOnClick}
            >
              {t("download.cta")}
            </Button>
          </div>
        </div>
      </div>

      <ModalDownloadApp
        qr={QRAppDownloadMockInterview}
        open={isModalOpen}
        onClose={handleOnClose}
      />
    </div>
  );
};

const Cloud = () => {
  const WIDTH_L = 1227;
  const WIDTH_S = 940;
  const { breakpoints } = useTheme();

  return (
    <div
      css={`
        position: relative;
        width: ${WIDTH_S}px;
        ${breakpoints.down("sm")} {
          width: ${WIDTH_L}px;
        }
      `}
    >
      <CloudCircle
        css={`
          left: 0;
        `}
      />
      <CloudCircle
        css={`
          right: ${WIDTH_S / 2}px;
          ${breakpoints.down("sm")} {
            right: ${WIDTH_L / 2}px;
          }
        `}
      />
      <CloudCircle
        css={`
          left: ${WIDTH_S / 2 - 300 / 2}px;
          ${breakpoints.down("sm")} {
            left: ${WIDTH_L / 2 - 398 / 2}px;
          }
        `}
      />
      <CloudCircle
        css={`
          left: ${WIDTH_S / 2}px;
          ${breakpoints.down("sm")} {
            left: ${WIDTH_L / 2}px;
          }
        `}
      />
      <CloudCircle
        css={`
          right: 0;
        `}
      />
    </div>
  );
};

interface CloudCircleProps {
  className?: string;
}

const CloudCircle: React.FC<CloudCircleProps> = ({ className }) => {
  const { breakpoints } = useTheme();

  return (
    <div
      css={`
        position: absolute;
        top: 0;
        border-radius: 50%;
        width: 300px;
        height: 304px;
        background-color: #e2f7ff;
        ${breakpoints.down("sm")} {
          width: 398px;
          height: 398px;
        }
      `}
      className={className}
    />
  );
};

const Resources = () => {
  const t = useTranslations("mockInterview");
  const { breakpoints } = useTheme();

  return (
    <div
      css={`
        margin: 0 auto 160px;
        padding: 0 20px;
        max-width: 1000px;
        ${breakpoints.down("sm")} {
          margin: 0 auto 100px;
        }
      `}
      id="section9"
    >
      <SectionTitle
        css={`
          ${breakpoints.down("sm")} {
            line-height: 34px;
            font-size: 24px;
          }
        `}
      >
        {t("resources.heading")}
      </SectionTitle>
      <Stack spacing="10px">
        <ResourceLink
          title="What can I expect at the marriage Green Card interview?"
          href="/resources/prepare-for-marriage-green-card-interview-and-common-questions#iii-what-can-i-expect-at-the-marriage-green-card-interview"
        />
        <ResourceLink
          title="Common marriage Green Card interview questions 2021"
          href="/resources/common-marriage-green-card-interview-questions-2021"
        />
        <ResourceLink
          title="How to prepare U.S. citizenship interview questions?"
          href="/resources/us-citizenship-interview-explanation#how-to-prepare-for-the-citizenship-interview"
        />
        <ResourceLink
          title="Common marriage U.S. citizenship interview questions 2021"
          href="/resources/naturalization-interview-questions"
        />
        <ResourceLink
          title="Top 5 mistakes during the visa interview"
          href="/resources/top-5-mistakes-during-the-u-s-visa-interview"
        />
      </Stack>
    </div>
  );
};

/**
 * ID of placeholder element for video player.
 */
const ID_VIDEO_PLAYER = "mockInterviewVideoPlayer";

const Video = () => {
  const t = useTranslations("mockInterview");
  const videoContainer = useRef<HTMLDivElement>(null);
  const playerRef = useRef(null);
  const { isIntersecting } = useIntersectionObserver(videoContainer, {
    threshold: 0.3,
  });
  const [isVideoReady, setIsVideoReady] = useState(false);

  const handleOnReady = useCallback(() => {
    // Autoplay is allowed if muted.
    playerRef.current?.mute();
    setIsVideoReady(true);
  }, []);

  const embedPlayer = useCallback(() => {
    return new (window as any).YT.Player(ID_VIDEO_PLAYER, {
      height: "370",
      width: "659",
      videoId: "6jWhUPrY_y0",
      playerVars: {
        autoplay: 1,
        loop: 1,
        playsinline: 1,
        playlist: "6jWhUPrY_y0",
      },
      events: {
        onReady: handleOnReady,
      },
    });
  }, [handleOnReady]);

  // Load YouTube Iframe API to embed a player.
  //   - https://developers.google.com/youtube/iframe_api_reference
  useEffect(() => {
    if ((window as any).YT?.Player) {
      // API is already ready.
      playerRef.current = embedPlayer();
    } else {
      // API is not ready. Define on-ready callback.
      (window as any).onYouTubeIframeAPIReady = () => {
        playerRef.current = embedPlayer();
      };
    }
  }, [embedPlayer]);
  useScript("https://www.youtube.com/iframe_api");

  // Play if in viewport, pause otherwise.
  useEffect(() => {
    if (isVideoReady) {
      if (isIntersecting) {
        playerRef.current?.playVideo();
      } else {
        playerRef.current?.pauseVideo();
      }
    }
  }, [isIntersecting, isVideoReady]);

  return (
    <div
      css={`
        margin: 0 auto 195px;
        max-width: 1000px;
        text-align: center;
        ${({ theme: { breakpoints } }) => breakpoints.down("sm")} {
          marign-bottom: 140px;
        }
      `}
    >
      <div
        css={`
          margin: 0 auto 60px;
          max-width: 870px;
          line-height: 50px;
          font-size: 38px;
          font-weight: 700;
          color: #999;
          ${({ theme: { breakpoints } }) => breakpoints.down("sm")} {
            marign-bottom: 40px;
            padding: 0 20px;
            line-height: 42px;
            font-size: 30px;
          }
        `}
      >
        {t("cloud.problem")}
      </div>
      <div
        css={`
          margin-bottom: 85px;
          line-height: 50px;
          font-size: 38px;
          font-weight: 700;
          ${({ theme: { breakpoints } }) => breakpoints.down("sm")} {
            margin-bottom: 60px;
            padding: 0 20px;
            line-height: 42px;
            font-size: 30px;
          }
        `}
      >
        {t("cloud.solution")}
      </div>

      <div
        css={`
          margin: 0 auto;
          padding: 0 20px;
          max-width: 659px;
        `}
        ref={videoContainer}
      >
        <div
          css={`
            position: relative;
            width: 100%;
            height: 0;
            padding-bottom: ${(370 / 659) * 100}%;
          `}
        >
          <div
            css={`
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              border-radius: 10px;
              background-color: #f1f3f5;
              overflow: hidden;
            `}
          >
            <div
              css={`
                width: 100%;
                height: 100%;
              `}
              id={ID_VIDEO_PLAYER}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const SectionTitle = styled.h2`
  margin-bottom: 60px;
  text-align: center;
  line-height: 56px;
  font-size: 40px;
  ${({ theme: { breakpoints } }) => breakpoints.down("sm")} {
    margin-bottom: 40px;
    line-height: 42px;
    font-size: 30px;
  }
`;
